/**
 * 甘特圖元件的公開型別。
 *
 * 本目錄下的所有檔案都是「純展示層」：props in / callbacks out，
 * 不得 import zustand、Tauri 或任何全域狀態。
 */

import type {
  Dependency,
  DependencyType,
  ISODateTime,
  Percent,
  TaskId,
} from '@bigint-project/core';

/** 時間軸縮放層級。 */
export type TimeScale = 'day' | 'week' | 'month' | 'quarter';

/**
 * 依賴線的顯示範圍。
 *
 * 相依任務一旦跨度拉長,連線會橫越整張圖,畫面很快就被線淹沒。因此除了「全部顯示」
 * 之外提供兩種收斂方式:只畫與目前選取任務相關的線,或完全不畫(改由表格的
 * 「前置任務」欄看關係)。
 */
export type DependencyLineMode = 'all' | 'selected' | 'none';

/**
 * 甘特圖列資料。由外層（store / selector）自 `Task` 攤平、依 WBS 排序、
 * 並過濾掉收合節點後產生，本元件不再做任何樹狀處理。
 */
export interface GanttTask {
  readonly id: TaskId;
  readonly name: string;
  /** WBS 碼，僅用於顯示與測試辨識。 */
  readonly wbs: string;
  /** 大綱層級，1 為最上層（用於摘要條的視覺縮排判斷）。 */
  readonly outlineLevel: number;
  readonly start: ISODateTime;
  readonly finish: ISODateTime;
  readonly percentComplete: Percent;
  readonly isSummary: boolean;
  readonly isMilestone: boolean;
  readonly isCritical: boolean;
  readonly isManuallyScheduled: boolean;
  /** 條右側顯示的資源名稱，例 "Alice, Bob"；無資源時為空字串。 */
  readonly resourceNames: string;
  /** 基準線對照；無基準時為 null。 */
  readonly baselineStart: ISODateTime | null;
  readonly baselineFinish: ISODateTime | null;
  /**
   * 落後預期進度的百分點；未落後（或未提供預期進度）時為 null。
   *
   * 刻意與 `isCritical` 分開：要徑是排程結構，落後是執行狀態，
   * 兩者可同時成立，共用紅色會讓「這條紅是什麼意思」無從判讀。
   */
  readonly behindPercent?: number | null;
}

export interface GanttChartProps {
  /** 已攤平、已依 WBS 排序、已含 collapsed 過濾的列。 */
  readonly tasks: readonly GanttTask[];
  readonly dependencies: readonly Dependency[];
  readonly timeScale: TimeScale;
  readonly rowHeight: number;
  readonly showBaseline: boolean;
  readonly showCriticalPath: boolean;
  /** 依賴線顯示範圍；未提供時全部顯示（維持既有行為）。 */
  readonly dependencyLineMode?: DependencyLineMode;
  /** 由外部控制，以便與左側表格同步捲動。 */
  readonly scrollTop: number;
  readonly onScrollTopChange: (value: number) => void;
  readonly onTaskDateChange: (
    taskId: TaskId,
    newStart: ISODateTime,
    newFinish: ISODateTime,
  ) => void;
  readonly onTaskProgressChange: (taskId: TaskId, percent: Percent) => void;
  readonly onDependencyCreate: (
    predecessorId: TaskId,
    successorId: TaskId,
    type: DependencyType,
  ) => void;
  readonly onTaskSelect: (taskId: TaskId, additive: boolean) => void;
  readonly selectedTaskIds: readonly TaskId[];

  // --- 以下為選用，皆有合理預設；主要供測試與嵌入情境覆寫 ---
  /** 視窗高度（px）。未提供時以 ResizeObserver 量測容器。 */
  readonly viewportHeight?: number;
  /** 視窗寬度（px）。未提供時以 ResizeObserver 量測容器。 */
  readonly viewportWidth?: number;
  /** 「今日」線的日期；未提供時使用執行當下。 */
  readonly today?: ISODateTime;
  /** 上下各多渲染幾列，降低捲動時的空白。 */
  readonly overscanRows?: number;
  readonly className?: string;
  /**
   * Ctrl/Cmd + 滾輪連續縮放時，「目前實際生效」的表頭刻度單位若跨過切點而改變，
   * 就會呼叫一次（同一刻度內連續縮放不會重複呼叫）。
   *
   * `timeScale` prop 是外部（下拉選單）最後一次明確選擇的刻度，滾輪縮放不會回頭
   * 改它；但滾輪縮放會讓「實際生效的刻度」與 `timeScale` 分岔，若外部完全不管，
   * 下拉選單就會顯示與表頭不一致的刻度。這個 callback 讓外部（例如下拉選單）能
   * 選擇跟著同步；不提供時行為與現在完全一致。
   */
  readonly onEffectiveTimeScaleChange?: (scale: TimeScale) => void;
}

/** 拖曳互動的種類。 */
export type DragKind = 'move' | 'resize' | 'progress' | 'link';

/** 依賴連線的端點（決定 FS/SS/FF/SF）。 */
export type LinkAnchor = 'start' | 'finish';

export const dependencyTypeFor = (
  predecessorAnchor: LinkAnchor,
  successorAnchor: LinkAnchor,
): DependencyType => {
  if (predecessorAnchor === 'finish') {
    return successorAnchor === 'start' ? 'FS' : 'FF';
  }
  return successorAnchor === 'start' ? 'SS' : 'SF';
};
